import React, { useState, useEffect } from 'react';
import { Modal, Input, TextArea, notification, Form, Button } from 'tea-component';
import useLanguage from '@op/hooks/useLanguage';
import './index.scss';

export interface ISavePromptModalProps {
  visible: boolean;
  mode: 'save' | 'edit';
  editingPromptId: string;
  initialPromptName: string;
  initialPromptContent: string;
  onClose: () => void;
  onSavePrompt?: (promptName: string, promptText: string) => Promise<any>;
  onUpdatePrompt?: (id: string, promptName: string, promptText: string) => Promise<any>;
}

const SavePromptModal: React.FC<ISavePromptModalProps> = ({
  visible,
  mode,
  editingPromptId,
  initialPromptName,
  initialPromptContent,
  onClose,
  onSavePrompt,
  onUpdatePrompt,
}) => {
  const { formatMessage } = useLanguage();
  const [promptName, setPromptName] = useState('');
  const [promptContent, setPromptContent] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [nameError, setNameError] = useState('');

  useEffect(() => {
    if (visible) {
      setPromptName(initialPromptName);
      setPromptContent(initialPromptContent);
      setNameError('');
    }
  }, [visible, initialPromptName, initialPromptContent]);

  const closeSaveModal = () => {
    onClose();
  };

  const handleSaveConfirm = async () => {
    if (!promptName.trim()) {
      notification.warning({
        description: formatMessage({ id: 'Please enter prompt name.' }),
      });
      return;
    }
    if (!promptContent.trim()) {
      notification.warning({
        description: formatMessage({ id: 'Please enter prompt.' }),
      });
      return;
    }

    setSaveLoading(true);
    try {
      if (mode === 'edit' && editingPromptId && onUpdatePrompt) {
        await onUpdatePrompt(editingPromptId, promptName.trim(), promptContent.trim());
      } else if (onSavePrompt) {
        await onSavePrompt(promptName.trim(), promptContent.trim());
      }
      closeSaveModal();
    } catch (error: any) {
      if (error?.code === 30001) {
        setNameError(formatMessage({ id: 'Name has repeated' }));
      } else {
        console.log(error);
      }
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <Modal
      visible={visible}
      caption={
        mode === 'edit'
          ? formatMessage({ id: 'Edit Prompt' })
          : formatMessage({ id: 'Save Prompt' })
      }
      onClose={closeSaveModal}
      className="dbo-prompt-save-modal"
      size={800}
      maskStyle={{
                zIndex: 1001,
              }}
    >
      <Modal.Body>
        <Form className="dbo-prompt-save-modal-form">
          <Form.Item
            required
            showStatusIcon={false}
            label={formatMessage({ id: 'Prompt Name' })}
          >
            <Input
              className="dbo-prompt-save-modal-input"
              value={promptName}
              onChange={(value) => {
                setPromptName(value);
                setNameError('');
              }}
              placeholder={formatMessage({ id: 'Please enter prompt name.' })}
              maxLength={200}
            />
            {nameError && (
              <div className="dbo-prompt-save-modal-error">{nameError}</div>
            )}
          </Form.Item>
          <Form.Item
            required
            showStatusIcon={false}
            label={formatMessage({ id: 'Prompt' })}
          >
            <TextArea
              className="dbo-prompt-save-modal-textarea"
              value={promptContent}
              onChange={setPromptContent}
              placeholder={formatMessage({ id: 'Please enter prompt.' })}
            />
          </Form.Item>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          className="dbo-prompt-save-modal-btn dbo-prompt-save-modal-btn--confirm"
          type="primary"
          onClick={() => { handleSaveConfirm() }}
          disabled={saveLoading || !promptName.trim() || !promptContent.trim() || nameError?.length > 0}
        >
          {formatMessage({ id: mode === 'edit' ? 'Confirm' : 'Save' })}
        </Button>
        <Button
          className="dbo-prompt-save-modal-btn dbo-prompt-save-modal-btn--cancel"
          onClick={closeSaveModal}
        >
          {formatMessage({ id: 'Cancel' })}
        </Button>

      </Modal.Footer>
    </Modal>
  );
};

export default SavePromptModal;
